import type {
  SyncEntityRef,
  SyncAckItem,
  SyncAttentionAction,
  SyncAttentionItem,
  SyncEnvelope,
  SyncEventInput,
  SyncLocalState,
  SyncMetaPatch,
  SyncRetryItem,
  SyncWireEntry,
} from './SyncBridgeTypes';
import type {
  SyncBootstrapResponse,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  SyncStatusResponse,
} from './SyncProtocol';

export type SyncRequestKind = 'network' | 'timeout' | 'http' | 'protocol';

/** Échec d'une requête de synchronisation, typé pour que le moteur choisisse la bonne réaction. */
export class SyncRequestError extends Error {
  constructor(
    message: string,
    readonly kind: SyncRequestKind,
    readonly statusCode?: number,
    readonly code?: string,
    readonly retry?: 'permanent' | 'transient' | 'conflict',
    readonly retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'SyncRequestError';
  }

  /** Le serveur n'a pas pu être joint (ou a répondu 502/503/504) : c'est un problème de réseau, pas de donnée. */
  get isConnectivity(): boolean {
    if (this.kind === 'network' || this.kind === 'timeout') return true;
    return this.kind === 'http' && [502, 503, 504].includes(this.statusCode ?? 0);
  }
}

/** Accès HTTP au serveur de synchronisation (`BusinessApiClient` en production, serveur de contrat en test). */
export interface SyncApiPort {
  status(opts?: { includeCounts?: boolean }): Promise<SyncStatusResponse>;
  push(request: SyncPushRequest): Promise<SyncPushResponse>;
  pull(params: { cursor?: string | null; limit?: number }): Promise<SyncPullResponse>;
  bootstrap(params: { resource: string; after?: string | null; limit?: number }): Promise<SyncBootstrapResponse>;
  enable(consentVersion: string): Promise<void>;
  disable(): Promise<void>;
  /** Renouvelle la session utilisateur ; faux si impossible (retour à l'écran de connexion, non bloquant). */
  refreshSession(): Promise<boolean>;
}

/** File d'envoi locale (processus principal), déjà liée à la société de la session. */
export interface SyncOutboxPort {
  getState(): Promise<SyncLocalState>;
  updateMeta(patch: SyncMetaPatch): Promise<SyncLocalState>;
  claimDue(max?: number, maxBytes?: number): Promise<SyncWireEntry[]>;
  resetSending(): Promise<{ reset: number }>;
  ack(results: SyncAckItem[]): Promise<{ merged: Array<{ resource: string; entity_id: string }> }>;
  markRetry(items: SyncRetryItem[], code: string, countAttempt?: boolean): Promise<{ ok: boolean }>;
  markRejected(
    opId: string,
    code: string,
    message: string,
    entity?: SyncEntityRef,
  ): Promise<{ ok: boolean; appliedServerVersion: boolean; refetchScheduled?: boolean }>;
  applyRemoteBatch(
    items: SyncEnvelope[],
    opts?: { nextCursor?: string | null; epoch?: string | null; markPull?: boolean },
  ): Promise<{ applied: Record<string, number>; skipped: Array<{ resource: string; entity_id: string }> }>;
  localCounts(): Promise<Record<string, number>>;
  enqueueAllLocal(): Promise<{ queued: number }>;
  initialMergeBatch(
    items: SyncEnvelope[],
    strategy?: 'server_wins' | 'local_wins',
  ): Promise<{ identical: number; replaced: number; downloaded: number; deferred: number }>;
  initialMergeFinish(cursor: string | null, epoch: string | null): Promise<{ queued: number }>;
  beginRebootstrap(mode: 'cursor_expired' | 'epoch_changed'): Promise<{ ok: boolean }>;
  reconcileSnapshot(
    presentKeys: string[],
    mode: 'cursor_expired' | 'epoch_changed',
    resources: string[],
  ): Promise<{ removed: number; resent: number }>;
  listAttention(): Promise<SyncAttentionItem[]>;
  resolveAttention(
    id: string,
    action: SyncAttentionAction,
  ): Promise<{ ok: boolean; payload: Record<string, unknown> | null; resource?: string }>;
  recordEvent(event: Omit<SyncEventInput, 'tenantId'>): Promise<{ ok: boolean }>;
}
